import { Button } from '@shared/components/shadcn';
import { twMerge } from '@shared/themes';
import Link from 'next/link';
import type { ComponentProps } from 'react';
import type { Option } from '../form-controls/types';
import { HorizontalScrollContainer } from '../HorizontalScrollContainer/HorizontalScrollContainer';
import { PulseNotification } from '../lotteries/PulseNotification/PulseNotification';

type ContextualNavigationLinkOption<T extends string = string> = Option<T> & {
  href: string;
};

type ContextualNavigationLinksProps = Omit<ComponentProps<'nav'>, 'children'> &
  Pick<ComponentProps<typeof Button>, 'size'> & {
    options: ContextualNavigationLinkOption<string>[];
    /** Current route selection; `null` when no option matches (no tab styled as active). */
    value: Option<string> | null;
  };

/**
 * Same presentation as ContextualNavigation, but each option is a Next.js `Link` instead of a button,
 * for URL-driven navigation (tabs/routes).
 *
 * @param options - Options including `href` per item
 * @param value - The option that matches the current route/selection (for styling and `aria-current`), or `null` if none match
 * @param size - Button size tokens (matches ContextualNavigation)
 * @param props - Props for the root `nav` element
 */
function ContextualNavigationLinks({ options, value, size, className, ...props }: ContextualNavigationLinksProps) {
  return (
    <nav className={twMerge('-my-3', className)} {...props}>
      <HorizontalScrollContainer className="py-3">
        {options.map(option => {
          const selected = value !== null && option.value === value.value;
          return (
            <Button key={option.value} asChild size={size} variant={selected ? 'primary' : 'secondary'} className="w-full relative">
              <Link
                href={option.href}
                aria-current={selected ? 'page' : undefined}
                data-active={selected ? 'true' : 'false'}
                data-testid={option['data-testid']}
                {...option.trackingProps}
              >
                {option.label}
                {option.showNotification ? <PulseNotification className="absolute -top-1 right-2.5" /> : null}
              </Link>
            </Button>
          );
        })}
      </HorizontalScrollContainer>
    </nav>
  );
}

export { ContextualNavigationLinks, type ContextualNavigationLinkOption, type ContextualNavigationLinksProps };
